import { ProductInfo } from "@redotech/redo-customer-event-common/customer-event-definition";
import type { Order } from "@redotech/redo-order-core-common/order";
import {
  GetOrdersRequest,
  ReturnRequest,
} from "@redotech/redo-return-core-common/return-flow";
import { AxiosResponse } from "axios";
import { RedoClient } from ".";

export class WidgetClient {
  constructor(
    private readonly redoClient: RedoClient,
    readonly widgetId: string,
  ) {}

  get client() {
    return this.redoClient.client;
  }

  get authorization() {
    return this.redoClient.authorization();
  }
}

export type CreateAuthRequest =
  | { type: "order"; email: string; orderNumber: string }
  | { type: "email"; token: string }
  | { type: "customer_widget"; customerWidgetAuthToken: string };

export interface CreateAuthResponse {
  token: string;
  customer: { email: string; name: string; customerId: string | undefined };
}

/*
 * GET /widgets/:widgetId/products/:id
 */
export async function getProduct(
  client: WidgetClient,
  { signal, productId }: { signal?: AbortSignal; productId: string | null },
): Promise<{
  variants: { id: number; inventory_quantity: number; [key: string]: any }[];
  id: number;
  images?: unknown[];
  title?: string;
  tags?: string;
}> {
  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/products/${productId}`,
    { signal, params: {} },
  );
  return response.data;
}

/**
 * POST /widgets/:widget_id/returns/submit
 *
 */
export async function submitReturn(
  client: WidgetClient,
  request: ReturnRequest,
  { signal }: { signal?: AbortSignal } = {},
): Promise<any> {
  const response = await client.client.post(
    `widgets/${encodeURIComponent(client.widgetId)}/returns/submit`,
    { params: request },
    { signal },
  );
  return response.data;
}

/**
 * GET /widgets/:widget_id/orders
 */
export async function getOrders(
  client: WidgetClient,
  request: GetOrdersRequest,
  { signal }: { signal?: AbortSignal } = {},
): Promise<any> {
  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/orders`,
    { signal, params: request },
  );
  return response.data;
}

/**
 * GET /widgets/:widget_id/orders/order_id/discount
 */
export async function getOrderDiscount(
  client: WidgetClient,
  orderId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<any> {
  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/orders/${encodeURIComponent(
      orderId,
    )}/discount`,
    { signal },
  );
  return response.data;
}

/**
 * POST /widgets/:widget_id/orders/order_id/discount
 */
export async function createOrderDiscount(
  client: WidgetClient,
  orderId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<any> {
  const response = await client.client.post(
    `widgets/${encodeURIComponent(client.widgetId)}/orders/${encodeURIComponent(
      orderId,
    )}/discount`,
    { signal },
  );
  return response.data;
}

/**
 * Get /widgets/:widget_id/orders/:orderId
 */
export async function getOrder(
  client: WidgetClient,
  orderId: string,
  {
    signal,
    includeUnfulfilled,
  }: { signal?: AbortSignal; includeUnfulfilled?: boolean } = {},
): Promise<{ order: Order }> {
  let headers = {};
  try {
    headers = client.authorization;
  } catch {
    // No token available, proceed without auth headers
  }

  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/orders/${orderId}${includeUnfulfilled ? "?includeUnfulfilled=true" : ""}`,
    { signal, headers },
  );
  return response.data;
}

export async function uploadImages(
  client: WidgetClient,
  params: any,
  { signal }: { signal?: AbortSignal } = {},
): Promise<any> {
  const response = await client.client.post(
    `widgets/${encodeURIComponent(client.widgetId)}/upload`,
    params,
    { signal },
  );
  return response.data;
}

export async function getShopifyLocations(
  client: WidgetClient,
  { signal }: { signal?: AbortSignal } = {},
): Promise<any> {
  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/shopify/locations`,
    { signal },
  );
  return response;
}

/**
 * GET /widget/:id/customers/top-products-of-interest
 */
export async function getTopProductsOfInterest(
  client: WidgetClient,
  {
    token,
    signal,
    limit,
  }: { token: string; signal?: AbortSignal; limit?: number },
) {
  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/customers/top-products-of-interest`,
    { params: { customerWidgetAuthToken: token, limit }, signal },
  );
  return response.data;
}

/**
 * GET /widget/:id/customers/viewed-products
 */
export async function getViewedProducts(
  client: WidgetClient,
  {
    token,
    signal,
    limit,
  }: { token: string; signal?: AbortSignal; limit?: number },
): Promise<ProductInfo[]> {
  const response = await client.client.get(
    `widgets/${encodeURIComponent(client.widgetId)}/customers/viewed-products`,
    { params: { customerWidgetAuthToken: token, limit }, signal },
  );
  return response.data;
}

/**
 * POST /customers/favorite-product
 */
async function setFavoriteProduct(
  client: WidgetClient,
  shouldSetAsFavoriteProduct: boolean,
  productHandle: string,
  token: string,
  variantId?: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<AxiosResponse> {
  const body = {
    productHandle,
    variantId,
    shouldSetAsFavorite: shouldSetAsFavoriteProduct,
  };
  const response = await client.client.post(
    `widgets/${encodeURIComponent(client.widgetId)}/customers/favorite-product`,
    body,
    { params: { customerWidgetAuthToken: token }, signal },
  );

  return response;
}

export async function addFavoriteProduct(
  client: WidgetClient,
  productHandle: string,
  token: string,
  variantId?: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<boolean> {
  const response = await setFavoriteProduct(
    client,
    true,
    productHandle,
    token,
    variantId,
    { signal },
  );
  return response.status === 200;
}

export async function removeFavoriteProduct(
  client: WidgetClient,
  productHandle: string,
  token: string,
  variantId?: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<boolean> {
  const response = await setFavoriteProduct(
    client,
    false,
    productHandle,
    token,
    variantId,
    { signal },
  );
  return response.status === 200;
}
